import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Travel Mor" className="w-24 h-24 object-contain" />
        </div>
        <h2 className="font-serif text-4xl font-light tracking-tighter mb-2 text-center">New password</h2>
        <p className="text-muted-foreground text-sm mb-10 text-center">Choose a strong new password.</p>

        {!token ? (
          <p className="text-center text-destructive-foreground" data-testid="reset-invalid">
            Invalid reset link.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-form">
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">New password</Label>
              <Input
                type="password"
                data-testid="reset-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="bg-input border-border h-12 focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Confirm password</Label>
              <Input
                type="password"
                data-testid="reset-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="bg-input border-border h-12 focus-visible:ring-primary"
              />
            </div>
            {error && <p className="text-sm text-destructive-foreground">{error}</p>}
            <Button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-medium hover:scale-[1.02] transition-transform gold-glow"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
        <Link to="/login" className="mt-6 text-muted-foreground text-sm flex items-center justify-center gap-2 hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
